import {ReplaySubject, Subject} from 'rxjs';
import {CameraEventName, cameraEventNames} from './cameraEvents';
import {assemblyDragEventNames} from './assemblyDragEvents';
import {editorPanelEventNames} from './editorPanelEvents';
import {themeEventNames} from './themeEvents';

/**
 * Viewer 相关事件注册区。
 *
 * 说明：
 * 1. 这里只集中登记事件名称，不承载业务逻辑；
 * 2. 后续迁移 Viewer 事件时，直接在该对象中新增 `事件名: new Subject()`。
 */
const viewerEvents: IEventBus.CadEventRegister = {};

/**
 * 命令相关事件注册区。
 *
 * 说明：
 * 命令状态、命令执行结果等事件后续统一登记在这里。
 */
const commandEvents: IEventBus.CadEventRegister = {};

/**
 * 草图相关事件注册区。
 *
 * 说明：
 * 草图绘制、草图选择与草图生命周期事件后续统一登记在这里。
 */
const sketchEvents: IEventBus.CadEventRegister = {};

/**
 * 相机相关事件注册区。
 *
 * 说明：
 * 相机投影模式、相机切换等视图事件统一登记在这里。
 * 只有完整相机状态事件保留最后一次载荷，使相机初始化后才创建的显示对象也能立即取得当前状态；
 * 其他相机通知仍维持普通 Subject 语义。
 */
const cameraEvents: IEventBus.CadEventRegister = Object.fromEntries(
    cameraEventNames.map((eventName) => [
        eventName,
        eventName === CameraEventName.StateChanged
            ? new ReplaySubject<IEventBus.CadEventPayload>(1)
            : new Subject<IEventBus.CadEventPayload>(),
    ]),
);

/**
 * 装配体拖拽相关事件注册区。
 *
 * 说明：
 * 零部件拖拽开始/停止事件，供 UI 层（如 AssemblyToolbarTab）订阅后更新选中态。
 */
const assemblyDragEvents: IEventBus.CadEventRegister = Object.fromEntries(
    assemblyDragEventNames.map((eventName) => [
        eventName,
        new Subject<IEventBus.CadEventPayload>(),
    ]),
);

/**
 * 编辑器右侧面板相关事件注册区。
 *
 * 说明：
 * 这里只登记跨包事件名称，实际右侧面板切换由 web-client 订阅消费。
 */
const editorPanelEvents: IEventBus.CadEventRegister = Object.fromEntries(
    editorPanelEventNames.map((eventName) => [
        eventName,
        new Subject<IEventBus.CadEventPayload>(),
    ]),
);
/**
 * 主题相关事件注册区。
 *
 * 说明：
 * 这里只登记主题变化事件名称，实际主题状态由宿主应用解析后派发。
 */
const themeEvents: IEventBus.CadEventRegister = Object.fromEntries(
    themeEventNames.map((eventName) => [
        eventName,
        new Subject<IEventBus.CadEventPayload>(),
    ]),
);

/**
 * CAD 事件注册表。
 */
export const EventsRegister: IEventBus.CadEventRegister = {
    ...viewerEvents,
    ...commandEvents,
    ...sketchEvents,
    ...cameraEvents,
    ...assemblyDragEvents,
    ...editorPanelEvents,
    ...themeEvents,
};

/**
 * 注册单个事件。
 *
 * @param eventName 事件名称。
 */
export const registerEvent = (eventName: IEventBus.CadEventName): void => {
    // 已注册事件保持原 Subject，避免重复注册时丢失已有订阅。
    if (EventsRegister[eventName]) {
        return;
    }

    EventsRegister[eventName] = new Subject<IEventBus.CadEventPayload>();
};

/**
 * 批量注册事件。
 *
 * @param eventNames 事件名称列表。
 */
export const registerEvents = (eventNames: IEventBus.CadEventName[]): void => {
    eventNames.forEach((eventName) => {
        registerEvent(eventName);
    });
};
